import { useState } from "react";

import AssignmentApi from "@/api/assignment-api";
import { ACTION, type AssessmentGridAction } from "@/components/assessment-grid/constants";
import Button from "@/components/common/input/button";
import Modal from "@/components/common/modal";
import CompetencyOverrideViewer from "@/components/competency-override-viewer";
import ContextMenu, { type ContextMenuOption } from "@/components/context-menu";
import CompetencyForm from "@/components/forms/competency";
import { logRejection, nl2br } from "@/helpers";
import type { Assignment } from "@/types/assignment";
import type { Competency } from "@/types/competency";

interface CompetencyListProps {
  assignment: Assignment;
  competencies: Competency[];
  dispatch: React.Dispatch<AssessmentGridAction>;
  edit_mode: boolean;
}

export default function CompetencyList({
  assignment,
  competencies,
  dispatch,
  edit_mode,
}: CompetencyListProps) {
  const [shared_abort_controller, _] = useState(new AbortController());

  const _updateCompetencies = (data: Competency | null) => {
    if (!data) return;
    dispatch({ type: ACTION.update_assignment_competencies, payload: data });
  };

  const _removeCompetency = ({
    assignment_id,
    competency_id,
  }: {
    assignment_id: string;
    competency_id: string;
  }) => {
    AssignmentApi.removeCompetencyFromAssignment(
      assignment_id,
      competency_id,
      shared_abort_controller.signal,
    )
      .then(({ error }) => {
        if (error) {
          console.error(error);
        } else {
          dispatch({
            type: ACTION.remove_assignment_competency,
            payload: competency_id,
          });
        }
      })
      .catch(logRejection);
  };

  return (
    <div className="flex flex-none flex-col gap-4 bg-bg-primary">
      {competencies.map((comp) => {
        const context_options: ContextMenuOption[] = [
          {
            icon: "settings",
            label: "Modify",
            form: (
              <CompetencyForm
                id={comp.id}
                assignment_id={assignment.id}
                callbackFn={_updateCompetencies}
              />
            ),
          },
          {
            icon: "play_circle",
            label: "View",
            form: (
              <CompetencyOverrideViewer competency_id={comp.id} assignment_id={assignment.id} />
            ),
          },
          {
            icon: "delete",
            label: "Remove",
            confirm: {
              body: "Remove competency",
              fn_args: {
                assignment_id: assignment.id,
                competency_id: comp.id,
              },
              confirmFn: _removeCompetency,
            },
          },
        ];

        return (
          <div key={comp.id} className="h-[3rem]">
            <div className="flex items-center font-bold">
              {edit_mode && (
                <span className="mr-2">
                  <ContextMenu options={context_options} right />
                </span>
              )}
              {comp.is_overridden && (
                <span
                  className="material-symbols-rounded notranslate pr-1 text-sm text-green-400"
                  title="This competency is specific for this assignment"
                >
                  stat_0
                </span>
              )}
              {comp.name}
            </div>
            <Modal>
              <p className="cursor-pointer text-sm text-future-purple">Show description</p>
              <DescriptionViewer description={comp.description} />
            </Modal>
          </div>
        );
      })}
    </div>
  );
}

interface DescriptionViewerProps {
  description: string;
  toggleFn?: () => void;
}
const DescriptionViewer = ({ description, toggleFn }: DescriptionViewerProps) => {
  return (
    <div className="w-[36rem] rounded-md bg-bg-primary p-8">
      {nl2br(description)}
      <Button aux_classes="mt-8" onClick={toggleFn}>
        Close
      </Button>
    </div>
  );
};
